<script lang="ts" setup>
  import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'

  import { useTheme } from 'vuetify'
  import colors from 'vuetify/util/colors'

  import { useFirebaseApp } from 'vuefire'

  import { getFunctions, httpsCallable } from 'firebase/functions'

  import * as am5 from '@amcharts/amcharts5'
  import * as am5xy from '@amcharts/amcharts5/xy'

  import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
  import am5themes_Material from '@amcharts/amcharts5/themes/Material'
  import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive'

  import useOuraAuth from '#web-oura-tools/auth'

  import { gcpRegion } from '#vue-shared-lib/utils/cloud-env'

  type MetricKey = 'eventCount' | 'activeUsers'

  type AnalyticsRow = {
    date: string
    eventCount: number
    activeUsers: number
  }

  const props = defineProps<{
    serviceId: string
    color?: string
  }>()

  const theme = useTheme()

  const fbApp = useFirebaseApp()

  const ouraAuth = useOuraAuth()

  const isLoading = ref(false)
  const loadError = ref<string | null>(null)

  const rows = ref<AnalyticsRow[]>([])
  const metric = ref<MetricKey>('eventCount')

  const chart = shallowRef<am5.Root | null>(null)
  const chartRef = useTemplateRef<HTMLElement>('chartRef')

  const envLabel = computed(() => ouraAuth.appEnv.value)

  async function fetchData() {
    isLoading.value = true
    loadError.value = null

    try {
      const functions = getFunctions(fbApp, gcpRegion)

      const callable = httpsCallable<{ serviceId: string; appEnv: string }, AnalyticsRow[]>(
        functions,
        'fetchModuleUsageStatistics',
      )

      const response = await callable({ serviceId: props.serviceId, appEnv: ouraAuth.appEnv.value })

      rows.value = response.data ?? []
    } catch (error) {
      console.warn('Failed to load module analytics', error)

      loadError.value = 'Could not load analytics data right now.'

      rows.value = []
    } finally {
      isLoading.value = false
    }
  }

  function renderChart() {
    if (!chartRef.value) return

    if (chart.value) {
      chart.value.dispose()
      chart.value = null
    }

    if (!rows.value.length) return

    const root = am5.Root.new(chartRef.value)

    const materialTheme = am5themes_Material.new(root)

    if (theme?.global?.current?.value?.dark) {
      materialTheme.rule('Label').setAll({ fill: am5.color('#ffffff') })
      materialTheme.rule('Grid').setAll({ stroke: am5.color('#ffffff') })
    }

    root.setThemes([am5themes_Animated.new(root), am5themes_Responsive.new(root), materialTheme])

    const colorMap = colors as unknown as Record<string, { lighten1: string; darken1: string }>
    const isDark = !!theme?.global?.current?.value?.dark
    const colorShade = colorMap[props.color ?? 'grey'] ?? colorMap.grey
    const seriesColor = am5.color(isDark ? colorShade.lighten1 : colorShade.darken1)

    const xyChart = root.container.children.push(
      am5xy.XYChart.new(root, { layout: root.verticalLayout, maxTooltipDistance: -1 }),
    )

    const tooltipFill = am5.color('#000000')
    const tooltipLabelFill = am5.color('#ffffff')

    const xAxisTooltip = am5.Tooltip.new(root, { getFillFromSprite: false })
    xAxisTooltip.get('background')!.setAll({ fill: tooltipFill, fillOpacity: 1 })
    xAxisTooltip.label.setAll({ fill: tooltipLabelFill })

    const xAxis = xyChart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 70 }),
        tooltip: xAxisTooltip,
        tooltipDateFormat: 'dd MMM yyyy',
      }),
    )

    const yAxis = xyChart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, {}),
      }),
    )

    xyChart.set('cursor', am5xy.XYCursor.new(root, {}))

    const metricLabel = metric.value === 'activeUsers' ? 'Unique users' : 'App opens'

    const series = xyChart.series.push(
      am5xy.LineSeries.new(root, {
        name: metricLabel,
        xAxis,
        yAxis,
        valueXField: 'timestamp',
        valueYField: metric.value,
        tooltip: am5.Tooltip.new(root, {
          getFillFromSprite: false,
          labelText: `{valueY} ${metricLabel}`,
        }),
      }),
    )

    series.set('stroke', seriesColor)
    series.set('fill', seriesColor)
    series.strokes.template.setAll({ strokeWidth: 2, stroke: seriesColor })

    series.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 4,
          fill: seriesColor,
        }),
      }),
    )

    const seriesTooltip = series.get('tooltip')!
    seriesTooltip.get('background')!.setAll({ fill: tooltipFill, fillOpacity: 1 })
    seriesTooltip.label.adapters.add('fill', () => tooltipLabelFill)

    const seriesData = rows.value.map((row) => ({
      timestamp: new Date(`${row.date}T00:00:00Z`).getTime(),
      eventCount: row.eventCount,
      activeUsers: row.activeUsers,
    }))

    series.data.setAll(seriesData)

    root._logo?.dispose()

    chart.value = root
  }

  watch(metric, renderChart)
  watch(() => props.serviceId, fetchData)
  watch(() => ouraAuth.appEnv.value, fetchData)
  watch(rows, renderChart, { flush: 'post' })

  onMounted(() => {
    fetchData()
  })

  onBeforeUnmount(() => {
    if (chart.value) {
      chart.value.dispose()
      chart.value = null
    }
  })
</script>

<template lang="html">
  <div class="service-analytics">
    <div v-if="isLoading" class="py-4 text-center">
      <v-progress-circular indeterminate size="24" />
    </div>

    <p v-else-if="loadError" class="text-warning my-2">{{ loadError }}</p>

    <template v-else-if="!rows.length">
      <p>No analytics data available yet for {{ envLabel }}.</p>

      <p class="text-caption text-medium-emphasis mb-1">
        Data comes from Google Analytics and can have up to a 48h delay
      </p>
    </template>

    <template v-else>
      <div class="d-flex align-center mb-2">
        <o-btn-toggle v-model="metric" mandatory>
          <v-btn text="App opens" value="eventCount" />
          <v-btn text="Unique users" value="activeUsers" />
        </o-btn-toggle>

        <v-spacer />

        <span class="text-capitalize text-medium-emphasis">{{ envLabel }} · Last 30 days</span>
      </div>

      <p class="text-caption text-medium-emphasis mb-1">
        Data comes from Google Analytics and can have up to a 48h delay
      </p>

      <div ref="chartRef" class="chart" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .chart {
    width: 100%;
    height: 220px;
  }
</style>
